import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEnhancedAuth } from '../../contexts/EnhancedAuthContext';
import { useEducationHub } from '../../contexts/EducationHubContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useEducationHubSidebar } from '../../hooks/useEducationHubSidebar';
import GuideSection from '../items/EducationHub/GuideSection';
import NewsSection from '../items/EducationHub/NewsSection';
import UpdatesSection from '../items/EducationHub/UpdatesSection';
import ResourcesSection from '../items/EducationHub/ResourcesSection';
import TemplatesSection from '../items/EducationHub/TemplatesSection';
import LegislationSection from '../items/EducationHub/LegislationSection';
import ContentViewer from '../items/EducationHub/ContentViewer';
import OverviewSection from '../items/EducationHub/OverviewSection';
import styles from '../../styles/EducationHub.module.css';

interface EducationHubProps {
  userDetails: any;
  userReady: boolean;
}

type ContentType = 'guide' | 'news' | 'update';

// Page title/subtitle per sidebar tab — each tab's own heading is promoted to
// the page-level header (matching Dashboard's single page title convention)
// instead of Education Hub showing a generic title on every tab.
const TAB_HEADERS: Record<string, { title: string; subtitle: string }> = {
  overview: {
    title: 'Education Hub',
    subtitle: 'Your central resource for learning, staying informed, and tracking your progress.',
  },
  guides: {
    title: 'Learning Guides',
    subtitle: 'Browse step-by-step guides to sharpen your real estate expertise.',
  },
  news: {
    title: 'Latest News & Announcements',
    subtitle: 'Stay up to date with market news and platform announcements.',
  },
  resources: {
    title: 'Educational Resources',
    subtitle: 'Reference materials and educational documents for real estate professionals.',
  },
  templates: {
    title: 'Document Templates',
    subtitle: 'Download templates, fill them out, and upload them to Document Management.',
  },
  legislation: {
    title: 'South African Property Law & Compliance',
    subtitle: 'Essential legal framework governing property practice, compliance, and client protection in South Africa.',
  },
  updates: {
    title: 'Product Updates & Release Notes',
    subtitle: "Release notes and product updates so you always know what's new.",
  },
};

const EducationHub: React.FC<EducationHubProps> = ({ userDetails, userReady }) => {
  const { isLoggedIn } = useEnhancedAuth();
  const { activeItem } = useSidebar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentContent, setCurrentContent] = useState<any>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  
  // Setup sidebar
  useEducationHubSidebar();

  // Use EducationHub context for all state
  const {
    guides,
    guidesLoading,
    fetchGuides,
    getGuideById,
    completions,
    getUserCompletions,
    news,
    newsLoading,
    fetchNews,
    getNewsById,
    updates,
    updatesLoading,
    fetchUpdates,
    getLatestUpdate,
    getUpdateById
  } = useEducationHub();
  
  const [quickStats, setQuickStats] = useState({
    totalGuides: 0,
    completedGuides: 0,
    totalNews: 0,
    latestUpdate: null as any
  });

  // Content viewer state
  const [viewingContent, setViewingContent] = useState<{
    type: ContentType;
    id: string;
  } | null>(null);


  // Handle closing content viewer
  const handleCloseContentViewer = () => {
    setSearchParams({});
    setViewingContent(null);
    setCurrentContent(null);
  };

  // Check URL parameters for content viewing
  useEffect(() => {
    const contentType = searchParams.get('type') as ContentType | null;
    const contentId = searchParams.get('id');
    
    if (contentType && contentId) {
      setViewingContent({ type: contentType, id: contentId });
    } else {
      setViewingContent(null);
      setCurrentContent(null);
    }
  }, [searchParams]);

  // Handle sidebar navigation when viewing content
  useEffect(() => {
    // If we're viewing content and a sidebar item is clicked, close the content viewer
    if (viewingContent && activeItem) {
      handleCloseContentViewer();
    }
    // eslint-disable-next-line
  }, [activeItem]);

  // Load content when viewing content changes
  useEffect(() => {
    if (!viewingContent || !userReady || !isLoggedIn) {
      return;
    }

    const loadContent = async () => {
      setContentLoading(true);
      setContentError(null);
      
      try {
        let content = null;
        
        switch (viewingContent.type) {
          case 'guide':
            content = await getGuideById(viewingContent.id);
            if (!content) setContentError('Guide not found');
            break;
          case 'news':
            content = await getNewsById(viewingContent.id);
            if (!content) setContentError('News post not found');
            break;
          case 'update':
            content = await getUpdateById(viewingContent.id);
            if (!content) setContentError('Update not found');
            break;
        }
        
        setCurrentContent(content);
      } catch (err) {
        console.error('Error loading content:', err);
        setContentError('Failed to load content');
      } finally {
        setContentLoading(false);
      }
    };

    loadContent();
  }, [viewingContent, userReady, isLoggedIn, getGuideById, getNewsById, getUpdateById]);

  // Load initial data
  useEffect(() => {
    if (userReady && isLoggedIn) {
      fetchGuides({ limit: 10 });
      fetchNews({ limit: 5, featured: true });
      fetchUpdates({ limit: 5 });
      if (userDetails?.id) {
        getUserCompletions();
      }
      getLatestUpdate().then(latest => {
        setQuickStats(prev => ({ ...prev, latestUpdate: latest }));
      }).catch(() => {
        setQuickStats(prev => ({ ...prev, latestUpdate: null }));
      });
    }
  }, [userReady, isLoggedIn, userDetails?.id, fetchGuides, fetchNews, fetchUpdates, getUserCompletions, getLatestUpdate]);

  // Update quick stats when data changes
  useEffect(() => {
    // Add safe checks to prevent TypeError when arrays are undefined
    const safeGuides = guides || [];
    const safeCompletions = completions || [];
    const safeNews = news || [];
    
    const completedCount = safeCompletions.filter(c => c.completed).length;
    setQuickStats(prev => ({
      ...prev,
      totalGuides: safeGuides.length,
      completedGuides: completedCount,
      totalNews: safeNews.length
    }));
  }, [guides, completions, news]);

  if (!userReady) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <div className={styles.loadingText}>Loading Education Hub...</div>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className={styles.title}>Education Hub</h1>
            <p className={styles.subtitle}>
              Access comprehensive guides, latest news, and product updates to enhance your real estate expertise.
            </p>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🔒</div>
              <h3 className={styles.emptyTitle}>Login Required</h3>
              <p className={styles.emptyDescription}>
                Please log in to access the Education Hub and start learning.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }


  const renderTabContent = () => {
    switch (activeItem) {
      case 'overview':
        return <OverviewSection quickStats={quickStats} />;
      case 'guides':
        return <GuideSection />;
      case 'news':
        return <NewsSection/>;
      case 'resources':
        return <ResourcesSection /> ;
      case 'templates':
        return <TemplatesSection userDetails={userDetails} /> ;
      case 'legislation':
        return <LegislationSection />;
      case 'updates':
        return <UpdatesSection />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Show ContentViewer if viewing content */}
        {viewingContent ? (
          <ContentViewer
            content={currentContent}
            contentType={viewingContent.type}
            loading={contentLoading}
            error={contentError}
            userDetails={userDetails}
            onBack={handleCloseContentViewer}
          />
        ) : (
          <>
            <div className={styles.header}>
              <h1 className={styles.title}>{(TAB_HEADERS[activeItem ?? 'overview'] ?? TAB_HEADERS.overview).title}</h1>
              <p className={styles.subtitle}>
                {(TAB_HEADERS[activeItem ?? 'overview'] ?? TAB_HEADERS.overview).subtitle}
              </p>
            </div>

            {/* Active Tab Content */}
            <div className={styles.sectionContent}>
              {renderTabContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EducationHub;
